import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import styles from './CopyLinkButton.module.css'

interface CopyLinkButtonProps {
  url?: string
  sectionId?: string
  language?: 'cs' | 'en'
  className?: string
}

export default function CopyLinkButton({
  url,
  sectionId,
  language = 'cs',
  className = '',
}: CopyLinkButtonProps) {
  const { pathname } = useLocation()
  const [status, setStatus] = useState<'idle' | 'pending' | 'copied' | 'failed'>('idle')
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const pending = useRef(false)
  const target =
    url ||
    `${window.location.origin}${pathname}${sectionId ? `#${encodeURIComponent(sectionId)}` : ''}`
  useEffect(() => () => clearTimeout(timer.current), [])

  const copy = async () => {
    if (pending.current) return
    pending.current = true
    clearTimeout(timer.current)
    setStatus('pending')
    try {
      await navigator.clipboard.writeText(target)
      setStatus('copied')
      timer.current = setTimeout(() => setStatus('idle'), 2500)
    } catch {
      setStatus('failed')
    } finally {
      pending.current = false
    }
  }

  return (
    <span className={`${styles.wrapper} ${className}`}>
      <button
        className={styles.copyLink}
        type="button"
        disabled={status === 'pending'}
        onClick={() => {
          void copy()
        }}
        aria-label={
          language === 'cs' ? 'Kopírovat odkaz na tuto část' : 'Copy a link to this section'
        }
      >
        {status === 'copied'
          ? language === 'cs'
            ? 'Zkopírováno'
            : 'Copied'
          : language === 'cs'
            ? 'Odkaz'
            : 'Link'}
      </button>
      <span className={styles.feedback} role="status">
        {status === 'copied' ? (language === 'cs' ? 'Odkaz zkopírován.' : 'Link copied.') : ''}
      </span>
      {status === 'failed' && (
        <span className={styles.failure} role="alert">
          {language === 'cs' ? 'Kopírování se nezdařilo. ' : 'Copy failed. '}
          <a href={target}>{language === 'cs' ? 'Otevřít odkaz' : 'Open link'}</a>
        </span>
      )}
    </span>
  )
}
